// TelcoTrack — ACL převodů mezi sklady (WarehouseTransfer / POST /transfer)
//
// Role sklad: libovolný směr.
// Ostatní: jen mezi osobními sklady (neosobní vyžaduje sklad).

import {
  hasOrgAdminRole,
  hasWarehouseApiAccessRole,
  hasWarehouseEditRole,
} from '../constants/roles.constants';
import {
  isPersonalWarehouse,
  type WarehouseUiGroupInput,
} from './warehouse-ui-group.util';
import {
  normalizeWarehouseIdList,
  WAREHOUSE_ISSUE_DENIED_OTHER_COMPANY_CS,
  type WarehouseIssueActor,
} from './warehouse-issue-acl.util';

export const WAREHOUSE_TRANSFER_DENIED_FROM_BRANCH_CS =
  'Převod zahrnující neosobní sklad smí provádět pouze role Sklad.';

export const WAREHOUSE_TRANSFER_DENIED_NO_ACCESS_CS =
  'Převody materiálu pouze pro role s přístupem ke skladům nebo roli Sklad.';

export interface WarehouseTransferAccessResult {
  allowed: boolean;
  reasonCs: string | null;
}

function inCompanyTransferScope(actor: WarehouseIssueActor, warehouseId: string): boolean {
  if (!Array.isArray(actor.companyWarehouseIds)) return true;
  const id = String(warehouseId ?? '').trim();
  if (!id) return false;
  if (id === String(actor.warehouseId ?? '').trim()) return true;
  return normalizeWarehouseIdList(actor.companyWarehouseIds).includes(id);
}

/**
 * Převod: edit role = libovolný směr (v rozsahu firmy, pokud je companyWarehouseIds nastaveno);
 * jinak jen osobní ↔ osobní (nebo firemní sklady v companyWarehouseIds pro org_admin).
 */
export function evaluateWarehouseTransferAccess(
  actor: WarehouseIssueActor,
  fromWarehouse: WarehouseUiGroupInput & { id: string },
  toWarehouse: WarehouseUiGroupInput & { id: string },
): WarehouseTransferAccessResult {
  const roles = actor.roles ? [...actor.roles] : [];

  if (
    !inCompanyTransferScope(actor, fromWarehouse.id) ||
    !inCompanyTransferScope(actor, toWarehouse.id)
  ) {
    return { allowed: false, reasonCs: WAREHOUSE_ISSUE_DENIED_OTHER_COMPANY_CS };
  }

  if (hasWarehouseEditRole(roles)) {
    return { allowed: true, reasonCs: null };
  }

  if (!hasWarehouseApiAccessRole(roles)) {
    return { allowed: false, reasonCs: WAREHOUSE_TRANSFER_DENIED_NO_ACCESS_CS };
  }

  const orgAdminHasBothInCompany =
    hasOrgAdminRole(roles) &&
    Array.isArray(actor.companyWarehouseIds) &&
    inCompanyTransferScope(actor, fromWarehouse.id) &&
    inCompanyTransferScope(actor, toWarehouse.id);

  if (
    !orgAdminHasBothInCompany &&
    (!isPersonalWarehouse(fromWarehouse) || !isPersonalWarehouse(toWarehouse))
  ) {
    return { allowed: false, reasonCs: WAREHOUSE_TRANSFER_DENIED_FROM_BRANCH_CS };
  }

  return { allowed: true, reasonCs: null };
}

export function canTransferStockBetweenWarehouses(
  actor: WarehouseIssueActor,
  fromWarehouse: WarehouseUiGroupInput & { id: string },
  toWarehouse: WarehouseUiGroupInput & { id: string },
): boolean {
  return evaluateWarehouseTransferAccess(actor, fromWarehouse, toWarehouse).allowed;
}
